import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Activity, Heart, Brain, Utensils, Flame, ChevronRight } from 'lucide-react';
import api from '../../services/api';
import WellnessTrendSparkline from '../WellnessTrendSparkline';
import { LEVEL_ICONS } from '../WellnessJourneyCard';
import { PILLAR_LABELS } from '../../lib/pillars';

/**
 * MobileHomeHero — the mobile Home hero (lg:hidden). A two-column top row:
 *   Left  → Wellness IQ composite ring (tap → /wellness-iq for full detail)
 *   Right → Wellness Journey level + XP + streak (tap → /journey)
 * Below: a compact row of the four pillar sub-rings (each taps to its pillar)
 * and the 7-day trend. This puts "open the app, see your score" front & centre.
 */

const PILLARS = [
  { key: 'movement',  label: PILLAR_LABELS.movement,  route: '/performance', color: '#CCFF00', Icon: Activity },
  { key: 'recovery',  label: PILLAR_LABELS.recovery,  route: '/recovery',    color: '#22d3ee', Icon: Heart },
  { key: 'mental',    label: PILLAR_LABELS.mental,    route: '/mental',      color: '#a855f7', Icon: Brain },
  { key: 'nutrition', label: PILLAR_LABELS.nutrition, route: '/nutrition',   color: '#fb923c', Icon: Utensils },
];

const overallColor = (s) => s == null ? '#52525b' : s >= 80 ? '#CCFF00' : s >= 60 ? '#facc15' : '#fb923c';

const MiniRing = ({ value, color, size = 44, Icon }) => {
  const r = size / 2 - 4;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value ?? 0));
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="-rotate-90 w-full h-full">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#27272a" strokeWidth="3.5" fill="none" />
        {value != null && (
          <circle
            cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth="3.5" fill="none"
            strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)}
            style={{ transition: 'stroke-dashoffset 800ms cubic-bezier(.2,.8,.2,1)' }}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <Icon className="w-4 h-4" style={{ color: value == null ? '#52525b' : color }} />
      </div>
    </div>
  );
};

const MobileHomeHero = () => {
  const navigate = useNavigate();
  const [iq, setIq] = useState(null);
  const [j, setJ] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = () => {
      api.getWellnessIQ().then((r) => { if (mounted) setIq(r.data); }).catch(() => {});
      api.getJourney().then((r) => { if (mounted) setJ(r.data); }).catch(() => {});
    };
    load();
    window.addEventListener('wellness:refresh', load);
    return () => { mounted = false; window.removeEventListener('wellness:refresh', load); };
  }, []);

  const overall = iq?.wellness_iq ?? null;
  const sub = iq?.sub_scores || {};
  const oColor = overallColor(overall);
  const R = 50;
  const C = 2 * Math.PI * R;
  const LvIcon = j ? (LEVEL_ICONS[j.level.index] || Activity) : Activity;

  return (
    <div className="lg:hidden space-y-3">
      {/* Two-column hero */}
      <div className="grid grid-cols-2 gap-3">
        {/* Wellness IQ */}
        <Link
          to="/wellness-iq"
          className="hpme-card p-4 flex flex-col items-center text-center active:scale-95 transition-transform animate-fade-up"
          style={{ animationDelay: '40ms' }}
          data-testid="mobile-hero-iq"
        >
          <div className="hpme-card-corner" />
          <p className="text-[9px] uppercase tracking-[0.2em] text-primary font-secondary font-bold mb-1.5">Wellness IQ</p>
          <div className="relative w-[120px] h-[120px]">
            <svg viewBox="0 0 120 120" className="-rotate-90 w-full h-full">
              <circle cx="60" cy="60" r={R} stroke="#27272a" strokeWidth="7" fill="none" />
              {overall != null && (
                <circle
                  cx="60" cy="60" r={R} stroke={oColor} strokeWidth="7" fill="none"
                  strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - Math.max(0, Math.min(100, overall)) / 100)}
                  style={{ transition: 'stroke-dashoffset 900ms cubic-bezier(.2,.8,.2,1)' }}
                />
              )}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-secondary font-bold text-4xl leading-none" style={{ color: oColor }}>
                {overall == null ? '—' : overall}
              </span>
            </div>
          </div>
          <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-zinc-400">
            Full picture <ChevronRight className="w-3 h-3" />
          </span>
        </Link>

        {/* Wellness Journey */}
        <Link
          to="/journey"
          className="hpme-card p-4 flex flex-col active:scale-95 transition-transform animate-fade-up"
          style={{ animationDelay: '120ms' }}
          data-testid="mobile-hero-journey"
        >
          <div className="hpme-card-corner" />
          <div className="flex items-center justify-between mb-2">
            <p className="text-[9px] uppercase tracking-[0.2em] text-primary font-secondary font-bold">Journey</p>
            {j?.streak_days > 0 && (
              <span className="flex items-center gap-0.5 text-orange-400 text-xs font-bold" data-testid="mobile-hero-streak">
                <Flame className="w-3.5 h-3.5 animate-streak-pulse" />{j.streak_days}d
              </span>
            )}
          </div>
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <span className="w-12 h-12 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center mb-2">
              <LvIcon className="w-6 h-6 text-primary" />
            </span>
            <p className="font-secondary font-bold text-base text-white leading-tight">
              {j ? `Lv ${j.level.index + 1}` : '—'}
            </p>
            <p className="text-[11px] text-zinc-400 leading-tight truncate w-full">{j?.level?.name || 'Building…'}</p>
          </div>
          {j && !j.level.is_max && (
            <div className="mt-2">
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all duration-700" style={{ width: `${j.level.pct}%` }} />
              </div>
              <p className="text-[10px] text-zinc-500 mt-1 text-center">
                {Math.max(0, (j.level.next_xp || 0) - j.total_xp)} XP to {j.level.next_name}
              </p>
            </div>
          )}
        </Link>
      </div>

      {/* Four pillar sub-rings */}
      <div className="hpme-card p-4 animate-fade-up" style={{ animationDelay: '200ms' }} data-testid="mobile-hero-pillars">
        <div className="hpme-card-corner" />
        <div className="grid grid-cols-4 gap-2">
          {PILLARS.map(({ key, label, route, color, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => navigate(route)}
              className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
              data-testid={`mobile-hero-pillar-${key}`}
            >
              <MiniRing value={sub[key]} color={color} Icon={Icon} />
              <span className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</span>
              <span className="font-secondary font-bold text-sm" style={{ color: sub[key] == null ? '#52525b' : color }}>
                {sub[key] == null ? '—' : Math.round(sub[key])}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* 7-day trend */}
      <div className="animate-fade-up" style={{ animationDelay: '280ms' }}>
        <WellnessTrendSparkline />
      </div>
    </div>
  );
};

export default MobileHomeHero;
